/**
 * SOFSEK — Live Instance Alert Feed (§13). Cross-application collision alerts:
 * the same subject account touched the same target entity on both sides of an
 * active conflict rule while both instances were open. Reviewed/escalated/
 * routed by a compliance manager; the underlying scan is re-run on demand.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { useAdaptiveList } from '../../lib/use_adaptive_list';
import { routedUpdate } from '../../lib/routed_writes';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { useTranslatorHydration, resolveNameForToken } from '../../lib/sfk_translator_hydrate';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search, Play } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.ALERT.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface AlertRow {
  alert_id: number;
  rule_id: number;
  app_user_account_id: number;
  entity_token: string;
  permission_id_a: number;
  permission_id_b: number;
  tracking_reference_a: string | null;
  tracking_reference_b: string | null;
  alert_status_code: string;
  disposition_note: string | null;
  detected_at: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'rule', header: 'Conflict Rule', defaultWidth: 200 },
  { key: 'account', header: 'Master User Token', defaultWidth: 180 },
  { key: 'entity_token', header: 'Target Entity', defaultWidth: 180 },
  { key: 'perm_a', header: 'Side A', defaultWidth: 200 },
  { key: 'perm_b', header: 'Side B', defaultWidth: 200 },
  { key: 'detected_at', header: 'Detected', defaultWidth: 170 },
  { key: 'status', header: 'Status', defaultWidth: 160 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function AlertInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rules, setRules] = useState<CodeOpt[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [perms, setPerms] = useState<Map<number, string>>(new Map());
  const [accounts, setAccounts] = useState<Map<number, string>>(new Map());
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string[]>(['OPEN']);
  const [disposing, setDisposing] = useState<AlertRow | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);

  // Small code lookups load once (rules + statuses resolve names / fill facets).
  const loadLookups = async () => {
    const [ruleRes, statusRes] = await Promise.all([
      apiDb.from('sfk_conflict_rule').select('rule_id, sfk_conflict_rule_tl ( locale_code, display_text )'),
      apiDb.from('sfk_alert_status').select('alert_status_code, sfk_alert_status_tl ( locale_code, display_text )'),
    ]);
    const err = ruleRes.error ?? statusRes.error;
    if (err) { setLookupError(err.message); return; }
    setRules(((ruleRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.rule_id), name: tlName(r.sfk_conflict_rule_tl, String(r.rule_id)) })));
    setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.alert_status_code as string, name: tlName(r.sfk_alert_status_tl, r.alert_status_code as string) })));
  };
  useEffect(() => { void loadLookups(); }, []);

  // Adaptive main list: instant client-side search under the cap, server-side above.
  const mapRow = useCallback((r: Record<string, unknown>) => r as unknown as AlertRow, []);
  const facets = useMemo(() => [{ column: 'alert_status_code', values: statusFacet }], [statusFacet]);
  const { rows, total, truncated, busy, error: listError, reload } =
    useAdaptiveList<Record<string, unknown>, AlertRow>({
      table: 'sfk_alert',
      columns: 'alert_id, rule_id, app_user_account_id, entity_token, permission_id_a, permission_id_b, tracking_reference_a, tracking_reference_b, alert_status_code, disposition_note, detected_at',
      order: { column: 'detected_at', ascending: false },
      search: filter,
      searchColumns: ['entity_token'],
      facets,
      map: mapRow,
    });
  const error = listError ?? lookupError;

  // Resolve ONLY the permission/account ids on the current page (scoped .in()),
  // never by preloading the full ~7,800-row action catalog (which truncates and
  // would leave names as raw ids past the cap).
  useEffect(() => {
    const permIds = [...new Set(rows.flatMap(r => [r.permission_id_a, r.permission_id_b]).filter((v): v is number => v != null))];
    const acctIds = [...new Set(rows.map(r => r.app_user_account_id).filter((v): v is number => v != null))];
    let cancelled = false;
    void (async () => {
      if (permIds.length) {
        const res = await apiDb.from('sfk_v_action').select('permission_id, display_name').in('permission_id', permIds);
        if (!cancelled && !res.error) setPerms(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.permission_id as number, r.display_name as string])));
      }
      if (acctIds.length) {
        const res = await apiDb.from('sfk_app_user_account').select('app_user_account_id, external_username, csm_user_master!app_user_id ( display_name )').in('app_user_account_id', acctIds);
        if (!cancelled && !res.error) setAccounts(new Map(((res.data ?? []) as Record<string, unknown>[]).map(r => [r.app_user_account_id as number, `${(r.csm_user_master as { display_name?: string } | null)?.display_name ?? ''} (${r.external_username as string})`])));
      }
    })();
    return () => { cancelled = true; };
  }, [rows]);

  const resolveNameForRule = (id: number) => rules.find(r => r.code === String(id))?.name ?? String(id);
  const resolveNameForAccount = (id: number) => accounts.get(id) ?? String(id);
  const resolveNameForPerm = (id: number) => perms.get(id) ?? String(id);
  const resolveNameForStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  // §17.B13 — related records for a collision alert: the rule it fired, the master
  // account, and the two conflicting permissions (Side A / Side B), resolved (§17.B4).
  const buildRelatedGroups = (r: AlertRow): RelatedChildGroup[] => [
    { key: 'rule', title: t('sfk.alert.related.rule', 'Conflict rule'),
      items: [{ id: r.rule_id, label: resolveNameForRule(r.rule_id) }] },
    { key: 'account', title: t('sfk.alert.related.account', 'Master account'),
      items: [{ id: r.app_user_account_id, label: resolveNameForAccount(r.app_user_account_id) }] },
    { key: 'perms', title: t('sfk.alert.related.perms', 'Conflicting permissions'),
      items: [
        { id: 'a', label: resolveNameForPerm(r.permission_id_a), title: t('sfk.alert.side_a', 'Side A') },
        { id: 'b', label: resolveNameForPerm(r.permission_id_b), title: t('sfk.alert.side_b', 'Side B') },
      ] },
  ];
  const hasRelated = (_r: AlertRow) => true;

  const tokens = useMemo(() => rows.flatMap(r => [r.entity_token, r.tracking_reference_a, r.tracking_reference_b].filter((v): v is string => !!v)), [rows]);
  const hydrated = useTranslatorHydration(tokens);

  const runScan = async () => {
    setScanning(true); setScanMsg(null); setLookupError(null);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_scan_transaction_collisions', { p_rule_id: null });
      if (e) throw new Error(e.message);
      setScanMsg(t('sfk.alert.scan_result', `${data ?? 0} new alert(s) found.`));
      reload();
    } catch (e) { setLookupError((e as Error).message); } finally { setScanning(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.alert.title', 'Live Instance Alert Feed')}
          description={t('sfk.alert.description', 'Cross-application collision alerts — the same account on both sides of an active conflict rule.')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => void runScan()} disabled={scanning} className="btn-primary flex items-center gap-1.5 disabled:opacity-60"><Play className="h-3.5 w-3.5" /> {scanning ? t('sfk.alert.scanning', 'Scanning…') : t('sfk.alert.run_scan', 'Run Collision Scan')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: total, label: t('sfk.alert.stat.total', 'Alerts') },
          { value: rows.length, label: t('sfk.alert.stat.shown', 'Shown') },
        ]} />
        {scanMsg && <div className="rounded-csm-card border border-csm-line bg-csm-surface-alt px-3 py-1.5 text-csm-xs text-csm-ink-soft">{scanMsg}</div>}
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.alert.facet.status', 'Status')} options={statuses.map(s => ({ value: s.code, label: s.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || statusFacet.length !== 1} onClear={() => { setFilter(''); setStatusFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {truncated && !busy && (
                <span className="text-csm-xs text-csm-muted">{t('common.list.truncated', 'Showing first {{n}} of {{total}} — refine your search', { n: rows.length, total })}</span>
              )}
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <Fragment key={r.alert_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.alert_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.alert_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'rule' && resolveNameForRule(r.rule_id)}
                        {col.key === 'account' && resolveNameForAccount(r.app_user_account_id)}
                        {col.key === 'entity_token' && resolveNameForToken(hydrated, r.entity_token)}
                        {col.key === 'perm_a' && resolveNameForPerm(r.permission_id_a)}
                        {col.key === 'perm_b' && resolveNameForPerm(r.permission_id_b)}
                        {col.key === 'detected_at' && formatDateTime(r.detected_at)}
                        {col.key === 'status' && resolveNameForStatus(r.alert_status_code)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                      {r.alert_status_code === 'OPEN' && canChange && (
                        <button onClick={() => setDisposing(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.alert.dispose', 'Dispose')}</button>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.alert_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records.')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {rows.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {disposing && (
        <DisposeAlertModal session={session} row={disposing} statuses={statuses.filter(s => s.code !== 'OPEN')}
          onClose={() => setDisposing(null)}
          onSaved={() => { setDisposing(null); void reload(); }} />
      )}
    </div>
  );
}

function DisposeAlertModal({ session, row, statuses, onClose, onSaved }: {
  session: AuthSession; row: AlertRow; statuses: CodeOpt[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(statuses[0]?.code ?? '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!status) throw new Error(t('sfk.alert.err.status_required', 'A disposition is required.'));
      const { error: e } = await routedUpdate(session, 'sfk_alert', 'alert_id', row.alert_id, {
        alert_status_code: status,
        disposition_note: note.trim() || null,
        disposed_at: new Date().toISOString(),
        disposed_by: getForensicActorId(session),
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.alert.dispose_title', 'Dispose Alert')}
      onClose={onClose}
      width="sm"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <PersonalizedField componentKey="modal" fieldKey="alert_status_code" label={t('sfk.alert.status', 'Disposition')}>
        <label className={labelCls}>{t('sfk.alert.status', 'Disposition')}</label>
        <select value={status} onChange={e => setStatus(e.target.value)} className={`${inputCls} bg-white`}>
          {statuses.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="disposition_note" label={t('sfk.alert.note', 'Note')}>
        <label className={labelCls}>{t('sfk.alert.note', 'Note')}</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} className="mb-3 w-full rounded border border-csm-border px-2 py-1 text-csm-sm" />
      </PersonalizedField>
      {error && <div className="mt-1 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkAlertPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <AlertInner session={session} />
    </PersonalizationProvider>
  );
}
